import type { Decor } from '../world/mapdef';
import { block, ellipse, hash, rect, shadow, shade, sun, type Ctx } from './paint';
import { wind } from './nature';
import { registerPropDrawers } from './propRegistry';
import { drawCharacter, type CharLook, type Pose } from './sprites';

/**
 * Beitou art pass (cks package, map beitou.ts): Japanese-era wooden inns and tea houses, people in
 * yukata, onsen signs and finger-posts, stone lanterns, maples, bamboo, hydrangeas, mossy stone walls,
 * rocks in the steaming stream and the green sulphur haze of Thermal Valley. Kinds use the `ck_bt`
 * prefix so they never collide with propsCks.ts.
 */

const CJK = '"PingFang TC", "Noto Sans TC", "Noto Sans CJK TC", "Microsoft JhengHei", sans-serif';
const isNight = () => sun.alpha < 0.8;

function txt(c: Ctx, s: string, x: number, y: number, size: number, col: string, weight = 900, shadowCol?: string): void {
  c.font = `${weight} ${size}px ${CJK}`;
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  if (shadowCol) {
    c.fillStyle = shadowCol;
    c.fillText(s, x + 0.4, y + 0.5);
  }
  c.fillStyle = col;
  c.fillText(s, x, y);
}

function poly(c: Ctx, col: string, pts: number[]): void {
  c.fillStyle = col;
  c.beginPath();
  c.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) c.lineTo(pts[i], pts[i + 1]);
  c.closePath();
  c.fill();
}

function line(c: Ctx, col: string, w: number, pts: number[]): void {
  c.strokeStyle = col;
  c.lineWidth = w;
  c.beginPath();
  c.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) c.lineTo(pts[i], pts[i + 1]);
  c.stroke();
}

const BITMAPS = new Map<string, { cv: HTMLCanvasElement; s: number }>();

/** Paint once per zoom step into an offscreen bitmap and blit it (`x0,y0,w,h` in world px). */
function cachedArt(c: Ctx, key: string, x0: number, y0: number, w: number, h: number, draw: (cc: Ctx) => void): void {
  const m = c.getTransform();
  const s = Math.min(3, Math.max(1, Math.round(Math.hypot(m.a, m.b) * 2) / 2));
  let e = BITMAPS.get(key);
  if (!e || e.s !== s) {
    const cv = document.createElement('canvas');
    cv.width = Math.ceil(w * s);
    cv.height = Math.ceil(h * s);
    const cc = cv.getContext('2d')!;
    cc.scale(s, s);
    cc.translate(-x0, -y0);
    draw(cc);
    e = { cv, s };
    BITMAPS.set(key, e);
  }
  c.drawImage(e.cv, x0, y0, w, h);
}

// ---------------------------------------------------------------------------
// Buildings
// ---------------------------------------------------------------------------

const HOUSES: [string, string, string][] = [
  ['瀧乃湯', '#4a5a66', '#f2ece0'], // the oldest public bath, grey tiles on white plaster
  ['北投茶屋', '#5a4a3a', '#e8dcc0'], // a tea house in dark timber
  ['湯元旅館', '#3a4a40', '#f6f0e4'], // an inn with a green-black roof
];

/** Japanese-era wooden house: tiled hip roof, engawa veranda, shoji windows, a noren over the door. */
function jpHouse(c: Ctx, d: Decor): void {
  const v = (d.variant ?? 0) % HOUSES.length, night = isNight();
  const [name, roof, wall] = HOUSES[v];
  const W = d.w ?? 104, x0 = d.x - W / 2, by = d.y;
  cachedArt(c, `btHouse${v}:${W}:${night ? 'n' : 'd'}:${Math.round(d.x)}`, x0 - 14, by - 78, W + 28, 86, (c) => {
    shadow(c, d.x, by, W / 2 + 6, 4, 0.3, 40);
    // Stone plinth and the raised wooden floor.
    block(c, x0 - 2, by - 6, W + 4, 6, '#8a8a84', '#a8a8a0', '#5a5a56');
    for (let i = x0; i < x0 + W; i += 9) rect(c, 'rgba(0,0,0,0.12)', i, by - 5, 0.6, 4);
    // Wall with dark timber frame.
    block(c, x0, by - 40, W, 34, wall, shade(wall, 0.2), shade(wall, -0.25));
    const timber = '#3a2a20';
    for (let i = x0 + 2; i < x0 + W; i += 18) rect(c, timber, i, by - 40, 1.6, 34);
    rect(c, timber, x0, by - 40, W, 1.6);
    rect(c, timber, x0, by - 24, W, 1.2);
    // Shoji windows: paper grids, warm at night.
    for (let i = x0 + 6; i < x0 + W - 22; i += 36) {
      const paper = night ? '#ffe9b0' : '#f8f4ea';
      block(c, i, by - 37, 14, 11, paper, '#ffffff', '#c8c0a8', '#5a4a3a');
      for (let k = 1; k < 4; k++) rect(c, '#8a7a62', i + k * 3.5, by - 36.4, 0.5, 9.8);
      rect(c, '#8a7a62', i + 0.6, by - 32.2, 12.8, 0.5);
      if (night) ellipse(c, 'rgba(255,220,150,0.25)', i + 7, by - 31, 12, 8);
    }
    // Door with a noren curtain and the name on it.
    const dx = d.x - 10;
    block(c, dx, by - 22, 20, 16, night ? '#ffd88a' : '#4a3a2a', undefined, undefined, timber);
    const noren = v === 1 ? '#2f4a8a' : v === 2 ? '#2f6a5a' : '#8a2a2a';
    for (let k = 0; k < 3; k++) block(c, dx + k * 6.8, by - 22, 6.2, 9, noren, shade(noren, 0.2), shade(noren, -0.2), shade(noren, -0.45));
    txt(c, name, d.x, by - 17.4, 3.2, '#fff6dc', 900);
    rect(c, timber, dx - 1, by - 22.8, 22, 1.2);
    // Engawa (veranda) with a rail and a lantern at each end.
    block(c, x0 - 6, by - 12, W + 12, 6, '#a8784a', '#c8986a', '#6a4428');
    for (let i = x0 - 4; i < x0 + W + 6; i += 12) rect(c, 'rgba(0,0,0,0.12)', i, by - 11, 0.6, 4);
    for (const lx of [x0 - 3, x0 + W + 1]) {
      rect(c, timber, lx, by - 24, 1.4, 12);
      block(c, lx - 2.5, by - 30, 6.4, 6, night ? '#fff0c0' : '#f4ecd8', '#ffffff', '#c8b890');
      rect(c, timber, lx + 0.4, by - 30, 0.6, 6);
      if (night) ellipse(c, 'rgba(255,220,150,0.3)', lx + 0.7, by - 27, 7, 6);
    }
    // Hip roof with upswept eaves, tile courses and a ridge.
    poly(c, shade(roof, -0.35), [x0 - 14, by - 40, x0 + W + 14, by - 40, x0 + W - 10, by - 62, x0 + 10, by - 62]);
    poly(c, roof, [x0 - 12, by - 41, x0 + W + 12, by - 41, x0 + W - 10, by - 61, x0 + 10, by - 61]);
    for (let i = x0 - 10; i < x0 + W + 10; i += 3) {
      const k = Math.abs(i - d.x) / (W / 2 + 10);
      rect(c, 'rgba(255,255,255,0.14)', i, by - 60 + k * 1.5, 0.8, 18 - k * 2);
      rect(c, 'rgba(0,0,0,0.2)', i + 1.4, by - 60 + k * 1.5, 0.6, 18 - k * 2);
    }
    for (let yy = by - 56; yy < by - 42; yy += 4.5) rect(c, 'rgba(0,0,0,0.15)', x0 - 8, yy, W + 16, 0.6);
    rect(c, 'rgba(255,255,255,0.3)', x0 - 12, by - 41.6, W + 24, 0.8);
    block(c, x0 + 8, by - 64, W - 16, 3.6, shade(roof, -0.2), shade(roof, 0.15));
    for (const s of [-1, 1]) {
      const ex = s < 0 ? x0 + 8 : x0 + W - 8;
      poly(c, shade(roof, -0.2), [ex, by - 64, ex + s * 4, by - 67, ex + s * 1.5, by - 60]);
    }
    // A small upper gable with a round window (the bathhouse) or a chimney (inn).
    if (v !== 1) {
      poly(c, shade(roof, -0.25), [d.x - 12, by - 61, d.x + 12, by - 61, d.x, by - 72]);
      ellipse(c, night ? '#ffe9b0' : '#9fc4d8', d.x, by - 64, 3, 3);
      rect(c, '#5a4a3a', d.x - 0.4, by - 67, 0.8, 6);
    }
    if (v === 2) block(c, x0 + W - 24, by - 74, 6, 12, '#6a6a64', '#8a8a84', '#4a4a46');
  });
  if (v === 2) {
    // Live steam from the inn's own hot-spring outlet.
    for (let i = 0; i < 3; i++) {
      const k = ((wind.time() * 0.25) + i / 3) % 1;
      ellipse(c, `rgba(255,255,255,${0.35 * (1 - k)})`, x0 + W - 21 + Math.sin(wind.time() + i) * 2 + k * 6, by - 76 - k * 18, 2 + k * 4, 1.5 + k * 2.5);
    }
  }
}

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

const YUKATA: CharLook[] = [
  { skin: '#f3cba6', hair: '#1d1a22', hairStyle: 'bun', top: '#2f4a8a', topShade: '#1f3468', bottom: '#2f4a8a', shoes: '#c8a060', dress: true, apron: '#e2493b' },
  { skin: '#f8d6b8', hair: '#3a2418', hairStyle: 'long', top: '#ff9fc0', topShade: '#d97a9a', bottom: '#ff9fc0', shoes: '#c8a060', dress: true, apron: '#ffd34e' },
  { skin: '#e8b48c', hair: '#1c1814', hairStyle: 'short', top: '#3a4a46', topShade: '#2a3a36', bottom: '#3a4a46', shoes: '#c8a060', dress: true, apron: '#8a6a3a' },
  { skin: '#f0c8a4', hair: '#9a948a', hairStyle: 'bun', top: '#7d5aa8', topShade: '#5e4282', bottom: '#7d5aa8', shoes: '#c8a060', dress: true, apron: '#f4f1ea', glasses: true },
  { skin: '#f6cfa8', hair: '#2a1d16', hairStyle: 'pigtails', top: '#57c278', topShade: '#3a9a58', bottom: '#57c278', shoes: '#c8a060', dress: true, apron: '#ffd34e', small: true },
  { skin: '#d9a47a', hair: '#141218', hairStyle: 'neat', top: '#f4f1ea', topShade: '#cfc8bb', bottom: '#f4f1ea', shoes: '#c8a060', dress: true, apron: '#2f3442' },
];
const FACING4 = ['down', 'up', 'left', 'right'] as const;

/** Someone in a yukata on the way to the baths, towel over the shoulder, bath basket in hand. */
function yukata(c: Ctx, d: Decor, t: number): void {
  const v = d.variant ?? 0;
  const look = YUKATA[Math.floor(v / 4) % YUKATA.length];
  const facing = FACING4[v % 4];
  const seed = hash(Math.round(d.x), Math.round(d.y));
  const pose: Pose = { facing, phase: 0, moving: false, blink: (t + seed * 5) % 4 < 0.12, breath: (t * 0.45 + seed) % 1 };
  drawCharacter(c, look, d.x, d.y, pose);
  const bob = pose.breath > 0.5 ? -1 : 0;
  const top = d.y - (look.small ? 12 : 16) - 3 + bob;
  // Folded towel on the shoulder and a wooden bath bucket.
  if (facing !== 'up') block(c, d.x - 6, top + 1, 4.4, 3, '#ffffff', '#ffffff', '#c8d8e0', '#8aa0a8');
  if (facing !== 'left') {
    const bx = d.x + 7;
    block(c, bx - 2.6, d.y - 6, 5.2, 4.4, '#c8a060', '#e0c080', '#8a6a3a');
    rect(c, '#8a6a3a', bx - 2.8, d.y - 4.6, 5.6, 0.6);
  }
  // Yukata pattern dots.
  const dy0 = top + 4;
  for (let k = 0; k < 4; k++) rect(c, 'rgba(255,255,255,0.55)', d.x - 3 + (k % 2) * 3.4, dy0 + Math.floor(k / 2) * 3, 0.9, 0.9);
}

// ---------------------------------------------------------------------------
// Signs and garden furniture
// ---------------------------------------------------------------------------

const ONSEN: [string, string, string][] = [['溫泉', '♨ ONSEN', '#8a2a2a'], ['地熱谷', 'THERMAL VALLEY', '#2f6a5a'], ['泡湯', 'HOT SPRING BATH', '#2f4a8a'], ['北投公園', 'BEITOU PARK', '#3a6a3a']];

/** A hot-spring sign: dark wood board on a post with ♨ and the name; a lantern lights it at night. */
function onsenSign(c: Ctx, d: Decor, t: number): void {
  const [zh, en, col] = ONSEN[(d.variant ?? 0) % ONSEN.length];
  const x = d.x, y = d.y, night = isNight();
  shadow(c, x, y, 5, 1.6);
  rect(c, '#3a2a20', x - 1.2, y - 30, 2.4, 30);
  block(c, x - 13, y - 44, 26, 16, '#4a3a2a', '#6a5a48', '#2a1a10', '#1b1410');
  rect(c, col, x - 12, y - 43, 24, 2);
  txt(c, zh, x, y - 36.6, zh.length > 2 ? 5.4 : 7, '#fff6dc', 900, 'rgba(0,0,0,0.4)');
  txt(c, en, x, y - 30.4, 2, '#e0c8a0', 700);
  // The ♨ steam mark, wobbling.
  for (let k = -1; k <= 1; k++) {
    const sx = x - 10 + k * 2.4 + (k === 0 ? 0 : 0), w = Math.sin(t * 2 + k) * 0.5;
    line(c, '#ffd34e', 0.6, [sx + w, y - 40.5, sx - w, y - 42.5, sx + w, y - 44.5]);
  }
  // Paper lantern hung from the post.
  rect(c, '#3a2a20', x + 1, y - 29, 7, 0.8);
  const ly = y - 28 + Math.sin(t * 1.4 + x) * 0.6;
  ellipse(c, night ? '#ffe9b0' : '#f4ecd8', x + 7.5, ly + 3.5, 2.8, 3.6);
  rect(c, '#8a2a2a', x + 5, ly + 3, 5, 0.5);
  if (night) ellipse(c, 'rgba(255,220,150,0.3)', x + 7.5, ly + 3.5, 8, 8);
}

/** A wooden finger-post with three arrows pointing to the sights. */
function signpost(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y;
  shadow(c, x, y, 3, 1.2);
  rect(c, '#5a4a3a', x - 1.4, y - 40, 2.8, 40);
  const arms: [string, number, number][] = [['地熱谷 →', -38, 1], ['← 圖書館', -31, -1], ['溫泉博物館 →', -24, 1]];
  for (const [label, dy, dir] of arms) {
    const w = 30;
    const L = dir > 0 ? x - 4 : x + 4 - w;
    poly(c, '#2a1a10', [L - 0.5, y + dy - 0.5, L + w + 0.5, y + dy - 0.5, L + w + (dir > 0 ? 3.5 : 0.5), y + dy + 3, L + w + 0.5, y + dy + 6.5, L - 0.5, y + dy + 6.5, L - (dir > 0 ? 0.5 : 3.5), y + dy + 3]);
    poly(c, '#8a6a3a', [L, y + dy, L + w, y + dy, L + w + (dir > 0 ? 3 : 0), y + dy + 3, L + w, y + dy + 6, L, y + dy + 6, L - (dir > 0 ? 0 : 3), y + dy + 3]);
    txt(c, label, L + w / 2, y + dy + 3.1, 2.6, '#fff6dc', 900);
  }
}

/** Granite garden lantern (春日燈籠) with a mossy base. */
function stoneLantern(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, night = isNight();
  shadow(c, x, y, 5, 1.8);
  block(c, x - 5, y - 4, 10, 4, '#8a8a84', '#a8a8a0', '#5a5a56');
  ellipse(c, '#5f8a4a', x - 3, y - 3.6, 2.4, 1.2);
  rect(c, '#9a9a94', x - 1.8, y - 16, 3.6, 12);
  rect(c, 'rgba(255,255,255,0.3)', x - 1.4, y - 16, 0.8, 12);
  block(c, x - 4.5, y - 18, 9, 2.4, '#9a9a94', '#b8b8b0', '#6a6a64');
  block(c, x - 3.6, y - 25, 7.2, 7, '#a8a8a0', '#c0c0b8', '#7a7a74');
  rect(c, night ? '#ffe9b0' : '#4a4a46', x - 1.6, y - 23.6, 3.2, 4);
  if (night) ellipse(c, 'rgba(255,220,150,0.3)', x, y - 21.6, 7, 6);
  poly(c, '#8a8a84', [x - 6.5, y - 25, x + 6.5, y - 25, x + 3, y - 29.5, x - 3, y - 29.5]);
  ellipse(c, '#8a8a84', x, y - 30, 1.4, 1.4);
}

/** A low dry-stone retaining wall with moss and ferns, along the hill terraces. */
function stoneWall(c: Ctx, d: Decor): void {
  const w = d.w ?? 64, L = d.x - w / 2, y = d.y;
  cachedArt(c, `btWall:${w}:${d.variant ?? 0}`, L - 2, y - 16, w + 4, 20, (c) => {
    block(c, L, y - 12, w, 12, '#8a8a84', '#a8a8a0', '#5a5a56', '#3a3a36');
    for (let row = 0; row < 3; row++) {
      const off = (row % 2) * 6;
      for (let i = -off; i < w; i += 12) {
        const tone = hash(i, row, 7);
        rect(c, tone < 0.3 ? '#9a9a94' : tone > 0.75 ? '#7a7a74' : '#8e8e88', L + Math.max(0, i) + 0.5, y - 11.5 + row * 4, Math.min(11, w - Math.max(0, i)) - 1, 3.2);
      }
      rect(c, 'rgba(0,0,0,0.25)', L, y - 8.4 + row * 4, w, 0.5);
    }
    for (let i = 3; i < w; i += 9 + Math.floor(hash(i, 1, 8) * 8)) {
      if (hash(i, 2, 8) < 0.5) ellipse(c, '#5f8a4a', L + i, y - 12 + hash(i, 3, 8) * 8, 2.4, 1.4);
      else {
        for (let k = -2; k <= 2; k++) line(c, k % 2 ? '#3f7a35' : '#6aa850', 0.8, [L + i, y - 12, L + i + k * 1.6, y - 16 - Math.abs(k) * -1.2]);
      }
    }
    rect(c, 'rgba(255,255,255,0.3)', L, y - 12, w, 0.8);
  });
}

// ---------------------------------------------------------------------------
// Plants
// ---------------------------------------------------------------------------

/** A Japanese maple: layered red-orange foliage on a slender trunk. */
function maple(c: Ctx, d: Decor, t: number): void {
  const v = d.variant ?? 0;
  const sway = wind.at(d.x, d.y) * 1.4;
  cachedArt(c, `btMaple:${v}`, d.x - 26, d.y - 46, 52, 52, (c) => {
    const x = d.x, y = d.y;
    shadow(c, x, y, 13, 2.6, 0.3, 30);
    block(c, x - 1.6, y - 20, 3.2, 20, '#5a4a3a', '#7a6a58', '#3a2a1a');
    line(c, '#5a4a3a', 1.2, [x, y - 18, x - 8, y - 28, x - 14, y - 32]);
    line(c, '#5a4a3a', 1.2, [x, y - 18, x + 8, y - 28, x + 14, y - 31]);
    const cols = v % 2 ? ['#b8302a', '#e8473a', '#ff7a4a'] : ['#c8502a', '#e8803a', '#ffb050'];
    const layers: [number, number, number, number][] = [[-12, -30, 11, 6], [12, -30, 11, 6], [-6, -36, 12, 6], [8, -36, 11, 6], [0, -41, 13, 6], [-16, -26, 7, 4], [17, -26, 7, 4]];
    for (const [bx, by, rx, ry] of layers) ellipse(c, cols[0], x + bx, y + by + 1.4, rx, ry);
    for (const [bx, by, rx, ry] of layers) ellipse(c, cols[1], x + bx, y + by, rx, ry);
    for (const [bx, by, rx, ry] of layers) ellipse(c, cols[2], x + bx - rx * 0.3, y + by - ry * 0.35, rx * 0.5, ry * 0.45);
    for (let i = 0; i < 18; i++) {
      const a = hash(i, v, 21) * Math.PI * 2, r = Math.sqrt(hash(i, v, 22));
      ellipse(c, cols[Math.floor(hash(i, v, 23) * 3)], x + Math.cos(a) * 20 * r, y - 34 + Math.sin(a) * 9 * r, 1.4, 1);
    }
  });
  for (let i = 0; i < 2; i++) {
    const k = (t * 0.2 + i / 2 + hash(Math.round(d.x), i)) % 1;
    ellipse(c, `rgba(232,72,58,${0.9 * (1 - k)})`, d.x + 10 - k * 24 + sway * 10 * k, d.y - 32 + k * 30, 1.3, 0.9);
  }
}

/** A clump of bamboo that sways in the wind. */
function bamboo(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, v = d.variant ?? 0;
  const sway = wind.at(x, y) * 2 + Math.sin(t * 0.8 + x) * 0.6;
  shadow(c, x, y, 7, 1.8, 0.25);
  for (let i = -2; i <= 2; i++) {
    const sx = x + i * 3 + hash(i, v, 1) * 2, h = 34 + hash(i, v, 2) * 18;
    const col = i % 2 ? '#7aa84a' : '#5f9a45';
    c.strokeStyle = col;
    c.lineWidth = 1.5;
    c.beginPath();
    c.moveTo(sx, y);
    c.quadraticCurveTo(sx + sway * 0.4, y - h * 0.6, sx + sway * (1 + i * 0.1), y - h);
    c.stroke();
    for (let k = 6; k < h; k += 7) rect(c, '#3f6a35', sx - 1 + sway * (k / h) * 0.9, y - k, 2.2, 0.8);
    for (let k = h * 0.4; k < h; k += 6) {
      const lx = sx + sway * (k / h), ly = y - k;
      const dir = (k / 6) % 2 ? 1 : -1;
      poly(c, k > h * 0.8 ? '#8ac060' : '#6aa850', [lx, ly, lx + dir * 6, ly - 2.5, lx + dir * 1.5, ly + 0.8]);
      poly(c, '#4f8a3f', [lx, ly + 2, lx - dir * 5, ly + 0.5, lx - dir * 1, ly + 3]);
    }
  }
}

/** A flowering hydrangea / azalea bush. */
function hydrangea(c: Ctx, d: Decor): void {
  const x = d.x, y = d.y, v = d.variant ?? 0;
  const bloom = ['#7a9ae0', '#ff8ab0', '#c89ae8', '#ffffff'][v % 4];
  ellipse(c, 'rgba(20,10,6,0.2)', x, y, 11, 3);
  ellipse(c, '#2f6a30', x, y - 5, 11, 6);
  ellipse(c, '#4a8a3a', x - 2, y - 6.5, 8, 4.4);
  for (let i = 0; i < 7; i++) {
    const bx = x + (hash(i, v, 31) - 0.5) * 18, by = y - 6 - hash(i, v, 32) * 5;
    ellipse(c, shade(bloom, -0.2), bx, by + 0.6, 2.8, 2.2);
    ellipse(c, bloom, bx, by, 2.6, 2);
    ellipse(c, 'rgba(255,255,255,0.5)', bx - 0.8, by - 0.8, 0.9, 0.7);
  }
}

// ---------------------------------------------------------------------------
// Water and haze
// ---------------------------------------------------------------------------

/** Rocks in the hot stream: dark, wet, with white water piling against them and a sulphur stain. */
function streamRocks(c: Ctx, d: Decor, t: number): void {
  const x = d.x, y = d.y, v = d.variant ?? 0;
  const rocks: [number, number, number, number][] = v % 2 ? [[-8, 0, 6, 4], [5, -2, 4.5, 3], [10, 3, 3, 2]] : [[-6, 1, 4, 3], [6, -1, 7, 4.4], [-1, 5, 3, 2]];
  for (const [rx, ry, rw, rh] of rocks) {
    const cx = x + rx, cy = y + ry;
    // Foam upstream (north) of each rock, breathing.
    const f = 0.45 + Math.sin(t * 3 + rx) * 0.15;
    ellipse(c, `rgba(255,255,255,${f})`, cx, cy - rh - 1, rw + 1.5, 1.6);
    ellipse(c, '#3a3a38', cx, cy - 1, rw, rh);
    ellipse(c, '#5a5a56', cx - rw * 0.2, cy - rh * 0.5 - 1, rw * 0.7, rh * 0.6);
    ellipse(c, '#c8b060', cx + rw * 0.3, cy - 0.6, rw * 0.4, rh * 0.3); // sulphur crust
    ellipse(c, 'rgba(255,255,255,0.4)', cx - rw * 0.4, cy - rh - 0.3, rw * 0.5, 0.6);
    ellipse(c, `rgba(255,255,255,${0.3 + Math.sin(t * 2.6 + ry) * 0.1})`, cx, cy + rh + 1.6, rw * 0.8, 1);
  }
}

/** Thermal Valley's green sulphur haze: a wide, slow, translucent sheet with brighter wisps. */
function valleyMist(c: Ctx, d: Decor, t: number): void {
  const w = d.w ?? 160, v = d.variant ?? 0;
  const night = isNight();
  for (let i = 0; i < 7; i++) {
    const k = (t * 0.02 + i / 7 + v * 0.13) % 1;
    const x = d.x - w / 2 + k * w + wind.at(d.x, d.y) * 8;
    const y = d.y - 4 - Math.sin(k * Math.PI) * 10 + Math.sin(t * 0.35 + i) * 2;
    const a = 0.3 * Math.sin(k * Math.PI);
    ellipse(c, night ? `rgba(150,230,200,${a * 0.8})` : `rgba(200,240,220,${a})`, x, y, 28 + (i % 3) * 8, 7 + (i % 2) * 2);
    ellipse(c, `rgba(255,255,255,${a * 0.5})`, x - 6, y - 2.5, 14, 3.5);
  }
  for (let i = 0; i < 5; i++) {
    const k = (t * 0.12 + i / 5 + v * 0.31) % 1;
    const x = d.x + (hash(i, v, 41) - 0.5) * w * 0.8 + k * 10;
    const y = d.y - k * 46;
    const a = 0.35 * Math.sin(k * Math.PI);
    ellipse(c, `rgba(236,250,244,${a})`, x, y, 7 + k * 10, 4 + k * 6);
  }
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

registerPropDrawers({
  decor: (c, d, t) => {
    switch (d.kind) {
      case 'ck_btJpHouse':
        jpHouse(c, d);
        return true;
      case 'ck_btYukata':
        yukata(c, d, t);
        return true;
      case 'ck_btOnsenSign':
        onsenSign(c, d, t);
        return true;
      case 'ck_btSignpost':
        signpost(c, d);
        return true;
      case 'ck_btToro':
        stoneLantern(c, d);
        return true;
      case 'ck_btStoneWall':
        stoneWall(c, d);
        return true;
      case 'ck_btMaple':
        maple(c, d, t);
        return true;
      case 'ck_btBamboo':
        bamboo(c, d, t);
        return true;
      case 'ck_btHydrangea':
        hydrangea(c, d);
        return true;
      case 'ck_btStreamRocks':
        streamRocks(c, d, t);
        return true;
      case 'ck_btMist':
        valleyMist(c, d, t);
        return true;
      default:
        return false;
    }
  },
  animatedDecor: ['ck_btJpHouse', 'ck_btYukata', 'ck_btOnsenSign', 'ck_btMaple', 'ck_btBamboo', 'ck_btStreamRocks', 'ck_btMist'],
});


